import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportCashExecFilterOptions } from "../report-cash-exec";
import { ReportCashExecFilterSettingsForm } from "../settings-form/ReportCashExecFilterSettingsForm";
import { ReportCashExecFilterSettings } from "../settings/ReportCashExecFilterSettings";
import { ReportCashExecRow } from "../table-data/ReportCashExecRow";
import { ReportCashExecJournalRequest } from "../journal/ReportCashExecJournalRequest";
import { ReportCashExecTableDataRequest } from "../data-request/ReportCashExecTableDataRequest";
import { Segmentation } from "../../../common/types/roots/fields";
import {Draggable} from "@/modules/budget/monitoring/reports-constructor/common/types/Draggable";

export class ReportCashExecFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportCashExecFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm    {
        // 1. Валидаций
        if (settings.reportName !== 'CASH-EXEC') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'CASH-EXEC') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }
        let dataSource2 = null
        if ('dataSource2' in settings) {
            dataSource2 = options.dataSources2.find(it => it.value === settings.dataSource2)
        } else {
            dataSource2 = options.dataSources.find(it => it.value === 'IISK')
        }
        if (!dataSource2) {
            throw new Error('error_not_found_data_source')
        }
        dataSource2 = { ...dataSource2 }


        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        let segmentations = settings.segmentations ?? cloneDeep(reportCashExecFilterOptions.segmentations)
        if (settings.classificationType === 'INCOMES') {
            segmentations = segmentations.filter(it => it.value !== "GU")
        }

        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let byFormOption = options.byForms.find(it => it.value === settings.byForm)
        if (!byFormOption) {
            throw new Error('error_not_found_by_form')
        }
        byFormOption = { ...byFormOption }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const developTypeOption = cloneDeep(options.developTypes.find(it => it.value === settings.developType) ?? null)



        // 2. Формирование
        const form: ReportCashExecFilterSettingsForm = {
            // Base:
            key: 'CASH-EXEC',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            dataSource2: dataSource2,
            date: new Date(settings.date),
            measureUnit: measureUnitOption,
            budgetTypes: budgetTypeOptions,
            statuses: statusOptions,
            isTree: settings.isTree,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            segmentations: segmentations,
            byForm: byFormOption,
            region: regionOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators),
            fillCbc: settings.fillCbc,
            developType: developTypeOption,
            totalFinSource: settings.totalFinSource,
            remainPeriodEqYear: settings.remainPeriodEqYear,
            kbkThisYearOnly: settings.kbkThisYearOnly,
            sortByRegionCode: settings.sortByRegionCode ?? false,
            msuAggregation: settings.msuAggregation ?? false,
            nameBeforeKbk: settings.nameBeforeKbk ?? false,
            calcCurPeriod: settings.calcCurPeriod ?? false,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'CASH-EXEC') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.byForm) {
            throw new Error('error_input_by_form')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        let segmentations = form.segmentations ?? cloneDeep(reportCashExecFilterOptions.segmentations)
        if (form.classificationType.value === 'INCOMES') {
            segmentations = segmentations.filter(it => it.value !== "GU")
        }

        const settings: ReportCashExecFilterSettings = {
            // Base:
            reportName: 'CASH-EXEC',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            dataSource2: form.dataSource2.value,
            date: strDate,
            budgetTypes: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            statuses: form.statuses.map(it => it.value),
            isTree: form.isTree,
            byForm: form.byForm.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: segmentations,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            incomeTotal: false,
            incomes: cloneDeep(form.incomes),
            expenseTotal: false,
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators),
            fillCbc: form.fillCbc ?? false,
            developType: form.developType.value ?? null,
            totalFinSource: form.totalFinSource,
            remainPeriodEqYear: form.remainPeriodEqYear,
            kbkThisYearOnly: form.kbkThisYearOnly,
            sortByRegionCode: form.sortByRegionCode ?? false,
            msuAggregation: form.msuAggregation ?? false,
            nameBeforeKbk: form.nameBeforeKbk ?? false,
            calcCurPeriod: form.calcCurPeriod ?? false,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportCashExecFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()

        const emptyForm: ReportCashExecFilterSettingsForm = {
            key: 'CASH-EXEC',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'CASH-EXEC')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields
            dataSource2: options.dataSources2.find(it => it.value === 'E-AKIMAT')!,
            date: yesterdayDate,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetTypes: [],
            region: null,
            mutuallyRedeeming: null,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            statuses: options.statuses.filter(it => it.value === '39'),
            isTree: true,
            byForm: options.byForms.find(it => it.value === '2-19')!,
            segmentations: cloneDeep(options.segmentations.filter(it => it.value !== 'GU')),
            roundUp: false,
            roundUpTo: null,
            incomes: cloneDeep(options.incomes),
            expenses: cloneDeep(options.expenses),
            indicators: cloneDeep(options.indicators),
            fillCbc: false,
            developType: options.developTypes.find(it => it.value === null)!,
            totalFinSource: false,
            remainPeriodEqYear: false,
            kbkThisYearOnly: true,
            sortByRegionCode: false,
            msuAggregation: false,
            nameBeforeKbk: false,
            calcCurPeriod: false,
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'CASH-EXEC') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_input_budget_types')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        let classifications: string[] = []
        const activeIncomes = form.incomes
            .filter(item => item.active)
        const activeExpenses = form.expenses
            .filter(item => item.active)

        const classificationType = form.classificationType.value
        if (classificationType === 'INCOMES') {
            classifications = activeIncomes.map(it => it.key)
        } else if (classificationType === 'EXPENSES') {
            classifications = activeExpenses.map(it => it.key)
        } else {
            throw new Error('error_unsupported_class_type')
        }

        const vzkList = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })
        if (cutness?.length > 0 && cutness.includes('KBK') && classifications.length === 0) {
            throw new Error('error_input_at_least_one_kbk')
        }


        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }
        const developType = form.developType.value


        const params: ReportCashExecTableDataRequest = {
            user_id: userId,
            incomes: form.incomes,
            expenses: form.expenses,
            source: form.dataSource.value,
            source2: form.dataSource2.value,
            repdate: strDate,
            region: form.region.value,
            cutness: cutness,
            mutually_redeeming: form.mutuallyRedeeming.value,
            vzk_list: vzkList,
            tree_view: form.isTree,
            form: form.byForm.value,
            budget_types: form.budgetTypes.map(it => it.value),
            status_list: form.statuses.map(it => it.value),
            income_total: false,
            expense_total: false,
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            locale: i18nService.locale,
            fill_cbc: form.fillCbc,
            develop_type: developType,
            total_fin_source: form.totalFinSource,
            remain_period_eq_year: form.remainPeriodEqYear,
            kbk_this_year_only: form.kbkThisYearOnly,
            sort_by_region_code: form.sortByRegionCode ?? false,
            msu_aggregation: form.msuAggregation ?? false,
            name_before_kbk: form.nameBeforeKbk ?? false,
            calc_cur_period: form.calcCurPeriod ?? false,
        }

        let endpoint = ''
        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)
        switch (classificationType) {
            case 'INCOMES': {
                endpoint = '2-19'
                break
            }
            case 'EXPENSES': {
                endpoint = '4-20';
                break;
            }
            default: throw new Error('error_type_classification')
        }

        const response = await fetch(`/api-py/monitoring/reports-constructor/cash-ececution/${endpoint}`,
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: any[] = await response.json()
        const firstClsn = classifications[0]
        const lastClsn = classifications[classifications.length - 1]
        const idMap:any = {}
        for (let i = 0; i < form.segmentations?.length; i++) {
            idMap[form.segmentations[i].value] = i
        }
        const tableData: ReportCashExecRow[] = responseTableData.map((row: any, index) => {
            row.index = index


            if (form.segmentations) {
                if (row.region) {
                    if (idMap['REGION'] > idMap['KBK']) {
                        row[lastClsn] = row.region
                    } else {
                        row[firstClsn] = row.region
                    }
                }
                if (row.gu) {
                    if (idMap['GU'] > idMap['KBK']) {
                        row[lastClsn] = row.gu
                    } else {
                        row[firstClsn] = row.gu
                    }
                }
            }


            if (!form.isTree && row[lastClsn]) {
                if (typeof row.name === 'string' && row.name) {
                    row.name = `${row[lastClsn]} - ${row.name}`
                } else {
                    row.name = row[lastClsn]
                }
            }

            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportCashExecJournalRequest = {
            reportName: "CASH-EXEC",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            columns_list: classifications,
            indicators_order: indicatorsOrder, // Может быть редактирована во время превью
            cutness: cutness,
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
